import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { jwtDecode } from 'jwt-decode';
import { pb } from '../../../helpers/pocketbase_helper';
import {
  VALIDATE_TOKEN,
  TOKEN_VALID,
  TOKEN_INVALID,
  TOKEN_EXPIRING,
  SHOW_EXPIRATION_WARNING
} from './actions';

// Constants
const TOKEN_EXPIRATION_WARNING_THRESHOLD = 5 * 60 * 1000; // 5 minutes in milliseconds

/**
 * Validates a JWT token and checks for expiration
 * @param {Object} action - Redux action with token payload
 */
function* validateTokenSaga(action) {
  try {
    const { token } = action.payload;

    if (!token) {
      yield put({
        type: TOKEN_INVALID,
        payload: { error: 'No token provided' }
      });
      return;
    }

    // Decode the JWT token
    const decodedToken = jwtDecode(token);
    const currentTime = Date.now();
    const expirationTime = decodedToken.exp * 1000; // Convert to milliseconds
    const timeRemaining = expirationTime - currentTime;

    // Check if token is expired
    if (timeRemaining <= 0) {
      yield put({
        type: TOKEN_INVALID,
        payload: { error: 'Token has expired' }
      });
      return;
    }

    // Check if token is expiring soon (within 5 minutes)
    if (timeRemaining <= TOKEN_EXPIRATION_WARNING_THRESHOLD) {
      yield put({
        type: TOKEN_EXPIRING,
        payload: {
          token,
          expiresAt: expirationTime,
          timeRemaining
        }
      });
      return;
    }

    // Token is valid and not expiring soon
    yield put({
      type: TOKEN_VALID,
      payload: {
        token,
        expiresAt: expirationTime
      }
    });

  } catch (error) {
    console.error('Token validation error:', error);
    yield put({
      type: TOKEN_INVALID,
      payload: { error: 'Invalid token format' }
    });
  }
}

/**
 * Checks token expiration from localStorage
 */
function* checkTokenExpirationSaga() {
  try {
    const authUserData = localStorage.getItem('authUser');
    
    if (!authUserData) {
      yield put({
        type: TOKEN_INVALID,
        payload: { error: 'No authentication data found' }
      });
      return;
    }

    const authUser = JSON.parse(authUserData);
    const token = authUser.token;

    if (!token) {
      yield put({
        type: TOKEN_INVALID,
        payload: { error: 'No token found in authentication data' }
      });
      return;
    }

    // Validate the token
    yield put({
      type: VALIDATE_TOKEN,
      payload: { token }
    });

  } catch (error) {
    console.error('Error checking token expiration:', error);
    yield put({
      type: TOKEN_INVALID,
      payload: { error: 'Error reading authentication data' }
    });
  }
}

/**
 * Handles token renewal process
 */
function* renewTokenSaga() {
  try {
    // For now, we'll just hide the warning
    // In a real implementation, this would trigger a refresh token flow
    yield put({
      type: 'HIDE_EXPIRATION_WARNING'
    });
  } catch (error) {
    console.error('Error renewing token:', error);
  }
}

/**
 * Main saga watcher
 */
export function* tokenValidationSaga() {
  yield takeEvery(VALIDATE_TOKEN, validateTokenSaga);
  yield takeLatest('CHECK_TOKEN_EXPIRATION', checkTokenExpirationSaga);
  yield takeLatest('RENEW_TOKEN', renewTokenSaga);
}

// Export individual sagas for testing
export { validateTokenSaga, checkTokenExpirationSaga, renewTokenSaga };
